import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiClient } from '../../api/config';
import { API_CONFIG } from '../../api/config';
import { logger } from '../../utils/logger';
import activityTracker from '../ActivityTracker';
import crossWindowSync from '../../utils/crossWindowSync';
import { trackLogin, trackSignUp } from '../../utils/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 初始化跨視窗同步
  useEffect(() => {
    crossWindowSync.init();

    // 監聽認證狀態變化
    crossWindowSync.on('auth_status', (authStatus) => {
      logger.log('收到跨視窗認證狀態更新:', authStatus);

      setIsAuthenticated(authStatus.isAuthenticated);
      setUser(authStatus.user);
      setError(authStatus.error || null);

      // 更新 ActivityTracker
      if (authStatus.user && authStatus.user.user_id) {
        activityTracker.setUserId(authStatus.user.user_id);
      } else {
        activityTracker.setUserId('0');
      }
    });

    // 檢查是否有現有的認證狀態
    const existingAuth = crossWindowSync.getCurrentAuthStatus();
    if (existingAuth && existingAuth.isAuthenticated && existingAuth.user) {
      logger.log('發現現有認證狀態，同步中...');
      setIsAuthenticated(existingAuth.isAuthenticated);
      setUser(existingAuth.user);
      setError(null);
      if (existingAuth.user.user_id) {
        activityTracker.setUserId(existingAuth.user.user_id);
      }
      setLoading(false);
    } else {
      // 沒有現有狀態，進行初始檢查
      checkAuthStatus();
    }

    return () => {
      crossWindowSync.off('auth_status');
    };
  }, []);

  // 检查认证状态
  const checkAuthStatus = async () => {
    try {
      logger.log('開始檢查認證狀態...');
      const response = await apiClient.get(API_CONFIG.endpoints.USER_AUTH.GET_USER);
      logger.log('認證狀態檢查成功:', response.data);

      const authStatus = {
        isAuthenticated: true,
        user: response.data,
        error: null
      };

      setUser(response.data);
      setIsAuthenticated(true);
      setError(null);
      
      // 如果沒有登入時間記錄，則記錄當前時間
      if (!localStorage.getItem('loginTime')) {
        localStorage.setItem('loginTime', Date.now().toString());
      }
      
      // 更新 ActivityTracker 的用户 ID
      activityTracker.setUserId(response.data.user_id);

      // 廣播認證狀態給其他視窗
      crossWindowSync.broadcastAuthStatus(authStatus);
    } catch (err) {
      logger.error('認證狀態檢查失敗:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });

      const authStatus = {
        isAuthenticated: false,
        user: null,
        error: err.response?.data?.message || '檢查認證狀態時發生錯誤'
      };

      setIsAuthenticated(false);
      setUser(null);
      if (err.response?.status !== 401) {
        setError(err.response?.data?.message || '檢查認證狀態時發生錯誤');
      }

      // 廣播未認證狀態給其他視窗
      crossWindowSync.broadcastAuthStatus(authStatus);
    } finally {
      setLoading(false);
    }
  };

  // 自动刷新令牌
  useEffect(() => {
    if (isAuthenticated) {
      // 檢查登入時間，如果超過7天則自動登出
      const checkLoginExpiry = () => {
        const loginTime = localStorage.getItem('loginTime');
        if (loginTime) {
          const loginTimestamp = parseInt(loginTime);
          const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7天
          const currentTime = Date.now();
          
          if (currentTime - loginTimestamp > sevenDaysInMs) {
            logger.log('登入已超過7天，自動登出');
            logout();
            return;
          }
        }
      };

      // 每小時檢查一次登入過期
      const expiryCheckInterval = setInterval(checkLoginExpiry, 60 * 60 * 1000);
      
      // 每7天刷新一次令牌
      const refreshInterval = setInterval(async () => {
        try {
          await apiClient.post(API_CONFIG.endpoints.USER_AUTH.REFRESH_TOKEN);
          logger.log('令牌刷新成功');
        } catch (err) {
          logger.error('刷新令牌失敗:', err);
          // 如果刷新失败，清除认证状态
          const authStatus = {
            isAuthenticated: false,
            user: null,
            error: '令牌刷新失敗'
          };

          setIsAuthenticated(false);
          setUser(null);
          crossWindowSync.broadcastAuthStatus(authStatus);
        }
      }, 7 * 24 * 60 * 60 * 1000); // 每7天刷新一次

      return () => {
        clearInterval(refreshInterval);
        clearInterval(expiryCheckInterval);
      };
    }
  }, [isAuthenticated]);

  // 登录
  const login = async (email, password) => {
    try {
      setLoading(true);
      logger.log('開始登錄流程...', { email });

      const response = await apiClient.post(API_CONFIG.endpoints.USER_AUTH.LOGIN, {
        email,
        password
      });

      logger.log('登錄成功:', response.data);
      const { user } = response.data;

      const authStatus = {
        isAuthenticated: true,
        user: user,
        error: null
      };

      setUser(user);
      setIsAuthenticated(true);
      
      // 記錄登入時間
      localStorage.setItem('loginTime', Date.now().toString());
      
      // 更新 ActivityTracker 的用户 ID
      activityTracker.setUserId(user.user_id);

      // 廣播登入狀態給其他視窗
      crossWindowSync.broadcastAuthStatus(authStatus);

      // 追踪 GA4 登入事件
      trackLogin('email');

      // 登录成功后立即检查认证状态
      await checkAuthStatus();

      return response.data;
    } catch (err) {
      logger.error('登錄失敗:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });

      const authStatus = {
        isAuthenticated: false,
        user: null,
        error: err.response?.data?.message || '登錄失敗'
      };

      setError(err.response?.data?.message || '登錄失敗');
      crossWindowSync.broadcastAuthStatus(authStatus);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 登出
  const logout = async () => {
    try {
      await apiClient.post(API_CONFIG.endpoints.USER_AUTH.LOGOUT);
      
      const authStatus = {
        isAuthenticated: false,
        user: null,
        error: null
      };

      setIsAuthenticated(false);
      setUser(null);
      setError(null);

      // 重置 ActivityTracker 的用户 ID
      activityTracker.setUserId('0');
      
      // 清除登入時間記錄
      localStorage.removeItem('loginTime');
      
      // 廣播登出狀態給其他視窗
      crossWindowSync.broadcastAuthStatus(authStatus);
    } catch (err) {
      setError(err.response?.data?.message || '登出失敗');
      throw err;
    }
  };

  // 注册
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await apiClient.post(API_CONFIG.endpoints.USER_AUTH.REGISTER, userData);
      
      const authStatus = {
        isAuthenticated: true,
        user: response.data.user,
        error: null
      };

      setUser(response.data.user);
      setIsAuthenticated(true);
      setError(null);
      
      // 記錄登入時間
      localStorage.setItem('loginTime', Date.now().toString());
      
      // 廣播註冊成功狀態給其他視窗
      crossWindowSync.broadcastAuthStatus(authStatus);
      
      // 追踪 GA4 注册事件
      trackSignUp('email');
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || '註冊失敗');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 更新个人资料
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await apiClient.put(API_CONFIG.endpoints.USER_AUTH.PROFILE, profileData);
      
      const authStatus = {
        isAuthenticated: true,
        user: response.data,
        error: null
      };

      setUser(response.data);
      setError(null);

      // 廣播更新後的用戶信息給其他視窗
      crossWindowSync.broadcastAuthStatus(authStatus);

      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || '更新個人資料失敗');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 重新发送验证邮件
  const resendVerification = async () => {
    try {
      setLoading(true);
      const response = await apiClient.post(API_CONFIG.endpoints.USER_AUTH.RESEND_VERIFICATION);
      setError(null);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || '發送驗證郵件失敗');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    register,
    updateProfile,
    resendVerification,
    checkAuthStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;